import React, { useState, useMemo, useEffect } from 'react';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';
import {
  AppState,
  AppAction,
  Role,
  UserProfile,
  Household,
  HouseholdMember,
  HouseholdReward,
  RewardRedemption,
  MemberPoints,
  FamilyGoal
} from '@/types';
import { initializeHousehold, joinHouseholdWithCode, leaveHousehold, transferHouseholdOwnership, deleteHousehold } from '../features/household-engine/components/householdService';
import { User } from 'firebase/auth'; // Import User type for initializeHousehold
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { db } from '@/firebase';
import {
  getDoc,
  setDoc,
  doc,
  updateDoc,
  deleteDoc,
  deleteField,
  arrayUnion,
  arrayRemove,
  getDocs,
  addDoc,
  collection,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import {
  Shield, Settings, Users, Home, Trash2, Mail,
  CheckCircle2, RefreshCw, Copy, XCircle, Plus, Clock,
  DoorOpen, ChevronDown, Loader2, Zap, LayoutGrid,
  ArrowRight, UserPlus, Building2, AlertCircle, BarChart2,
  CreditCard, Star, Trophy, Gift, Check, X as XIcon, Target, PartyPopper
} from 'lucide-react';
import { SubscriptionTier } from '@everyday-os/shared';
import { useHouseholdAnalytics } from '../hooks/useHouseholdAnalytics';
import { DowngradeWarningModal } from '@/components/ui/DowngradeWarningModal';

// Per-tier resource limits (mirrors enforceFreeTierLimitsOnWrite CF)
const PLAN_LIMITS: Record<string, { maxActiveTasks: number; maxMembers: number }> = {
  FREE:    { maxActiveTasks: 20,   maxMembers: 2  },
  BASIC:   { maxActiveTasks: 500,  maxMembers: 6  },
  PREMIUM: { maxActiveTasks: 5000, maxMembers: 12 },
};

export const HouseholdSettings: React.FC<{ state: AppState; dispatch: React.Dispatch<AppAction> }> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justUpgraded = searchParams.get('upgraded') === 'true';
  const justManaged = searchParams.get('managed') === 'true';
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  const handleOpenBillingPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const fns = getFunctions(getApp());
      const createPortal = httpsCallable(fns, 'createPortalSession');
      const result = await createPortal({});
      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch {
      showToast('Failed to open billing portal. Please try again.', 'error');
      setIsOpeningPortal(false);
    }
  };



  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'MEMBERS' | 'SWITCHER' | 'ANALYTICS' | 'REWARDS'>(state.currentHouseholdId ? 'OVERVIEW' : 'SWITCHER');

  // Rewards state
  const [rewards, setRewards] = useState<HouseholdReward[]>([]);
  const [redemptions, setRedemptions] = useState<RewardRedemption[]>([]);
  const [memberPoints, setMemberPoints] = useState<MemberPoints[]>([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [showAddReward, setShowAddReward] = useState(false);
  const [newRewardName, setNewRewardName] = useState('');
  const [newRewardEmoji, setNewRewardEmoji] = useState('🎁');
  const [newRewardCost, setNewRewardCost] = useState('100');
  const [isSavingReward, setIsSavingReward] = useState(false);
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isCreatingHousehold, setIsCreatingHousehold] = useState(false); 
  const [isJoining, setIsJoining] = useState(false);
  const [maxHouseholdsPerUser, setMaxHouseholdsPerUser] = useState<number>(3);
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [newHouseholdNameError, setNewHouseholdNameError] = useState<string | null>(null); // 🌟 NEW State for input error
  const [joinCodeError, setJoinCodeError] = useState<string | null>(null);
  const [showOwnershipTransferPrompt, setShowOwnershipTransferPrompt] = useState(false);
  const [newOwnerCandidateId, setNewOwnerCandidateId] = useState<string | null>(null);
  const [isLeavingHousehold, setIsLeavingHousehold] = useState(false); // New loading state for leaving
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [commuteBaseMinutes, setCommuteBaseMinutes] = useState<number>(0);
  const [isSavingCommute, setIsSavingCommute] = useState(false);

  // Family Goals state
  const [familyGoals, setFamilyGoals] = useState<FamilyGoal[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [isSavingGoal, setIsSavingGoal] = useState(false);
  const [newGoal, setNewGoal] = useState<{ title: string; targetCount: number; targetFrequency: 'weekly' | 'bi_weekly' | 'monthly'; sessionDurationMins: number }>({ title: '', targetCount: 10, targetFrequency: 'monthly', sessionDurationMins: 60 });

  const currentUserId = state.currentUser?.id || '';

  const currentUser = useMemo(() => {
    // Create a mock Firebase User object from UserProfile for householdService functions
    if (state.currentUser && currentUserId && state.currentUser.email) {
      return {
        uid: currentUserId,
        email: state.currentUser.email,
        displayName: state.currentUser.name || state.currentUser.email,
        photoURL: state.currentUser.avatar || '',
        // Add other properties if householdService explicitly uses them (though uid, email, displayName are most common)
      } as User;
    }
    return null;
  }, [state.currentUser, currentUserId]);



  // 🛰️ Household Lookup - Now correctly handles `undefined` if no currentHouseholdId
  const household: Household | undefined = useMemo(() => {
    if (!state.currentHouseholdId) {
      return undefined;
    }
    return (state.households || []).find((h: Household) => h.id === state.currentHouseholdId);
  }, [state.households, state.currentHouseholdId]);

  // DERIVED MEMBERS FOR DISPLAY (This is the core fix)
  const householdMembersForDisplay = useMemo(() => {
    if (!household || !state.userProfiles) return [];

    return household.memberIds.map(memberId => {
      const memberFromHousehold = household.members.find(m => m.userId === memberId);
      const userProfile = state.userProfiles.find(p => p.id === memberId); // Still useful for other dynamic user profile data
      // Get role from household.permissions, defaulting if not found
      const memberRole = household.permissions?.[memberId]?.role || Role.MEMBER;

      // Get joinedAt from permissions or use a placeholder/current date
      const memberJoinedAt = memberFromHousehold?.joinedAt || new Date().toISOString(); // Prioritize from memberFromHousehold

      return {
        id: memberId, // Ensure 'id' is always present for HouseholdMember
        userId: memberId,
        name: memberFromHousehold?.name || userProfile?.name || 'Unknown User',
        email: memberFromHousehold?.email || userProfile?.email || 'N/A',
        role: memberRole,
        joinedAt: memberJoinedAt,
        avatar: memberFromHousehold?.avatar || userProfile?.avatar, // Prioritize from memberFromHousehold
      } as HouseholdMember;
    });
  }, [household, state.userProfiles]);


  // 📊 Analytics data — called unconditionally (React hook rules)
  const analyticsHouseholdId = household?.id ?? null;
  const analyticsMemberIds = household?.memberIds ?? [];
  const {
    taskCompletionByDay,
    contributionByMember,
    spendByCategory,
    loading: analyticsLoading,
  } = useHouseholdAnalytics(analyticsHouseholdId, analyticsMemberIds);
  const analyticsMaxTask = Math.max(...taskCompletionByDay.map(d => d.total), 1);
  const analyticsMaxMember = Math.max(...contributionByMember.map(m => m.score), 1);
  const analyticsMaxCategory = Math.max(...spendByCategory.map(c => c.count), 1);

  // 🛡️ REVISED: Derive current user role directly from household.permissions
  const currentUserRole: Role = useMemo(() => {
    if (!household || !currentUserId) {
      return Role.MEMBER; // Default to MEMBER if no household or user ID
    }
    return household.permissions?.[currentUserId]?.role || Role.MEMBER;
  }, [household?.permissions, currentUserId, household]);

  const isOwner = currentUserRole === Role.OWNER;
  const isAuthorized = isOwner || currentUserRole === Role.ADMIN;

  const activeInvite = useMemo(() => {
    if (!household?.activeInvite) return null;
    const invite = household.activeInvite;
    const expiry = new Date(invite.expiresAt).getTime();
    return Date.now() > expiry ? null : invite;
  }, [household]);

  useEffect(() => {
    const fetchLimit = async () => {
      try {
        const limitsDoc = await getDoc(doc(db, 'settings', 'limits'));
        if (limitsDoc.exists()) {
          const data = limitsDoc.data();
          setMaxHouseholdsPerUser(data.maxHouseholdsPerUser || 2);
        }
      } catch (error) {
        console.error("Error fetching household limit:", error);
      }
    };
    fetchLimit();
  }, []);

  useEffect(() => {
    if (activeTab !== 'REWARDS' || !household?.id) return;
    setRewardsLoading(true);
    Promise.all([
      getDocs(query(collection(db, 'households', household.id, 'rewards'), orderBy('createdAt', 'desc'))),
      getDocs(query(collection(db, 'households', household.id, 'rewardRedemptions'), orderBy('requestedAt', 'desc'))),
      getDocs(query(collection(db, 'households', household.id, 'memberPoints'), orderBy('monthlyPoints', 'desc'))),
    ]).then(([rSnap, rdSnap, mpSnap]) => {
      setRewards(rSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
      setRedemptions(rdSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
      setMemberPoints(mpSnap.docs.map(d => ({ ...(d.data() as any) })));
    }).finally(() => setRewardsLoading(false));
  }, [activeTab, household?.id]);

  // Fetch memberPoints when MEMBERS tab is active (for XP display)
  useEffect(() => {
    if (activeTab !== 'MEMBERS' || !household?.id) return;
    getDocs(query(collection(db, 'households', household.id, 'memberPoints'), orderBy('monthlyPoints', 'desc')))
      .then(mpSnap => setMemberPoints(mpSnap.docs.map(d => ({ ...(d.data() as any) } as MemberPoints))));
  }, [activeTab, household?.id]);

  // Sync commuteBaseMinutes from household data
  useEffect(() => {
    if (household) setCommuteBaseMinutes((household as any).commuteBaseMinutes ?? 0);
  }, [household?.id]);

  const handleSaveCommuteMinutes = async () => {
    if (!household?.id || !isAuthorized) return;
    setIsSavingCommute(true);
    try {
      await updateDoc(doc(db, 'households', household.id), { commuteBaseMinutes });
      dispatch({ type: 'UPDATE_HOUSEHOLD', payload: { ...(household as any), commuteBaseMinutes } });
    } catch (err) {
      console.error('Failed to save commuteBaseMinutes', err);
    } finally {
      setIsSavingCommute(false);
    }
  };

  // Load family goals when household changes
  useEffect(() => {
    if (!household?.id) return;
    setGoalsLoading(true);
    getDocs(collection(db, 'households', household.id, 'familyGoals'))
      .then(snap => setFamilyGoals(snap.docs.map(d => ({ id: d.id, ...d.data() } as FamilyGoal))))
      .catch(err => console.error('Failed to load family goals:', err))
      .finally(() => setGoalsLoading(false));
  }, [household?.id]);

  const handleAddGoal = async () => {
    if (!household?.id || !state.currentUser || !newGoal.title.trim()) return;
    setIsSavingGoal(true);
    try {
      const goalData = {
        householdId: household.id,
        title: newGoal.title.trim(),
        targetCount: newGoal.targetCount,
        completedCount: 0,
        targetFrequency: newGoal.targetFrequency,
        membersRequired: [] as string[],
        sessionDurationMins: newGoal.sessionDurationMins,
        createdBy: state.currentUser.id,
        createdAt: new Date().toISOString(),
      };
      const ref = await addDoc(collection(db, 'households', household.id, 'familyGoals'), goalData);
      setFamilyGoals(prev => [...prev, { id: ref.id, ...goalData }]);
      setShowAddGoal(false);
      setNewGoal({ title: '', targetCount: 10, targetFrequency: 'monthly', sessionDurationMins: 60 });
    } catch (err) {
      console.error('Failed to add family goal:', err);
    } finally {
      setIsSavingGoal(false);
    }
  };

  const handleIncrementGoal = async (goal: FamilyGoal) => {
    if (!household?.id) return;
    const newCount = goal.completedCount + 1;
    await updateDoc(doc(db, 'households', household.id, 'familyGoals', goal.id), { completedCount: newCount });
    setFamilyGoals(prev => prev.map(g => g.id === goal.id ? { ...g, completedCount: newCount } : g));
  };

  const handleAddReward = async () => {
    if (!newRewardName || !household?.id || !state.currentUser) return;
    setIsSavingReward(true);
    try {
      await addDoc(collection(db, 'households', household.id, 'rewards'), {
        name: newRewardName, emoji: newRewardEmoji, pointCost: parseInt(newRewardCost) || 100,
        isActive: true, householdId: household.id, createdBy: state.currentUser.id, createdAt: serverTimestamp(),
      });
      setNewRewardName(''); setNewRewardEmoji('🎁'); setNewRewardCost('100');
      setShowAddReward(false);
      const snap = await getDocs(collection(db, 'households', household.id, 'rewards'));
      setRewards(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    } finally { setIsSavingReward(false); }
  };

  const handleRedeem = async (reward: HouseholdReward) => {
    if (!household?.id || !state.currentUser) return;
    await addDoc(collection(db, 'households', household.id, 'rewardRedemptions'), {
      rewardId: reward.id, rewardName: reward.name, pointCost: reward.pointCost,
      memberId: state.currentUser.id, memberName: (state.currentUser as any).displayName || state.currentUser.id,
      status: 'pending', householdId: household.id, requestedAt: new Date().toISOString(),
    });
    const snap = await getDocs(collection(db, 'households', household.id, 'rewardRedemptions'));
    setRedemptions(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
  };

  const handleResolveRedemption = async (id: string, status: 'approved' | 'rejected') => {
    if (!household?.id) return;
    await updateDoc(doc(db, 'households', household.id, 'rewardRedemptions', id), { status, resolvedAt: new Date().toISOString() });
    setRedemptions(prev => prev.map(r => r.id === id ? { ...r, status } : r));
  };

  // --- ACTIONS ---
  const handleSwitchHousehold = (id: string) => {
    dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: id });
    setActiveTab('OVERVIEW');
  };

  const handleCreateInvite = async () => {
    if (!household || !isAuthorized || !currentUserId) return;
    setIsCreatingInvite(true);
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      await setDoc(doc(db, "invites", code), {
        code, householdId: household.id, householdName: household.name,
        expiresAt, createdBy: currentUserId, createdAt: new Date().toISOString()
      });
      await updateDoc(doc(db, "households", household.id), {
        activeInvite: { code, expiresAt, createdBy: currentUserId, createdAt: new Date().toISOString() }
      });
    } catch (e) { console.error(e); } finally { setIsCreatingInvite(false); }
  };

  // New handler for creating a household
  const handleInitializeNewHousehold = async () => {

    setNewHouseholdNameError(null);

    if (!currentUser || !currentUserId) {

      showToast('You must be logged in to create a household.', 'error');
      return;
    }

    if (!newHouseholdName.trim()) {

      setNewHouseholdNameError('Household name cannot be empty.');
      return;
    }

    const userHouseholdsCount = state.households.filter(h => h.memberIds.includes(currentUserId)).length;
    if (userHouseholdsCount >= maxHouseholdsPerUser) {

      setNewHouseholdNameError(`You have reached the maximum limit of ${maxHouseholdsPerUser} households.`);
      return;
    }

    setIsCreatingHousehold(true);
    try {

      const newHousehold = await initializeHousehold(currentUser, newHouseholdName.trim());


      dispatch({ type: 'ADD_HOUSEHOLD', payload: newHousehold });
      dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: newHousehold.id });
      setActiveTab('OVERVIEW');
      setNewHouseholdName('');
      showToast(`Household "${newHousehold.name}" created successfully!`, 'success');
    } catch (e: any) {
      console.error('handleInitializeNewHousehold: Error creating household:', e); // Detailed error logging

      setNewHouseholdNameError(t('household.error_creating_household', `Failed to create household: ${e.message || 'Please try again.'}`));
    } finally {
      setIsCreatingHousehold(false);
    }
  };

  const handleJoinByCode = async () => {
    setJoinCodeError(null);

    if (!joinCode.trim()) {
      setJoinCodeError('Invite code cannot be empty.');
      return;
    }

    if (!currentUser || !currentUserId) {
      showToast('You must be logged in to join a household.', 'error');
      return;
    }

    const userHouseholdsCount = state.households.filter(h => h.memberIds.includes(currentUserId)).length;
    if (userHouseholdsCount >= maxHouseholdsPerUser) {
      setJoinCodeError(`You have reached the maximum limit of ${maxHouseholdsPerUser} households.`);
      setJoinCode('');
      return;
    }

    setIsJoining(true);
    try {
      const joinedHousehold = await joinHouseholdWithCode(currentUser, joinCode.trim());

      dispatch({ type: 'ADD_HOUSEHOLD', payload: joinedHousehold });
      dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: joinedHousehold.id });
      setActiveTab('OVERVIEW');
      setJoinCode('');
      showToast(`Welcome to "${joinedHousehold.name}"!`, 'success');
    } catch (e: any) {
      console.error('Error joining household:', e);


      setJoinCodeError(`Failed to join household: ${e.message || 'Please check the invite code and try again.'}`);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveHousehold = async () => {
    if (!household || !currentUserId) return;

    setIsLeavingHousehold(true);
    try {
      if (isOwner) {
        if (household.memberIds.length === 1) {
          // Owner is the only member, prompt to delete household
          if (window.confirm(t('household.confirm_delete_alone_owner', 'You are the only member. Leaving this household will permanently delete it and all its data. Are you sure?'))) {
            await deleteHousehold(household.id, currentUserId);
            dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: null });
            dispatch({ type: 'REMOVE_HOUSEHOLD', payload: household.id });
            setActiveTab('SWITCHER');
            showToast(t('household.household_deleted_success', 'Household deleted successfully.'), 'success');
          }
        } else {
          // Owner with other members, prompt to transfer ownership
          setShowOwnershipTransferPrompt(true);
        }
      } else {
        // Non-owner member or admin leaving
        if (window.confirm(t('household.confirm_leave_household', 'Are you sure you want to leave this household?'))) {
          await leaveHousehold(household.id, currentUserId);
          if (state.currentHouseholdId === household.id) {
            dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: null });
          }
          dispatch({ type: 'REMOVE_HOUSEHOLD', payload: household.id });
          setActiveTab('SWITCHER');
          showToast(t('household.left_household_success', 'You have left the household.'), 'success');
        }
      }
    } catch (e: any) {
      console.error('Error handling leave household:', e);
      showToast(e.message || t('household.error_leaving_household', 'Failed to leave household. Please try again.'), 'error');
    } finally {
      setIsLeavingHousehold(false);
    }
  };

  const handleTransferOwnership = async () => {
    if (!household || !currentUserId || !newOwnerCandidateId) return;

    setIsLeavingHousehold(true); // Use same loading state
    try {
      if (window.confirm(t('household.confirm_transfer_ownership', `Are you sure you want to transfer ownership to ${householdMembersForDisplay.find(m => m.userId === newOwnerCandidateId)?.name}? You will become a regular member.`))) {
        await transferHouseholdOwnership(household.id, currentUserId, newOwnerCandidateId);
        // After transfer, the old owner (current user) becomes a regular member.
        // The UI should reflect this, potentially by refetching household data or updating state more granularly.
        // For simplicity, we'll just alert and perhaps refresh relevant parts of the state.
        showToast(t('household.ownership_transferred_success', 'Ownership transferred successfully! You are now a member.'), 'success');
        setShowOwnershipTransferPrompt(false);
        setNewOwnerCandidateId(null);
        // Force a re-render or re-fetch of household data to update roles
        // For now, a simple dispatch might be enough if household data is re-fetched on app state update.
        // A more robust solution might involve refetching the current household directly.
        dispatch({ type: 'UPDATE_HOUSEHOLD', payload: { id: household.id, updates: { ownerId: newOwnerCandidateId, /* potentially update permissions object */ } } as any }); // This dispatch type might need refinement
      }
    } catch (e: any) {
      console.error('Error transferring ownership:', e);
      showToast(e.message || t('household.error_transferring_ownership', 'Failed to transfer ownership. Please try again.'), 'error');
    } finally {
      setIsLeavingHousehold(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!household) return;
    const member = householdMembersForDisplay.find(m => m.userId === memberId);
    if (!window.confirm(
      t('household.confirm_remove_member', `Remove ${member?.name || 'this member'} from the household?`)
    )) {
      return;
    }

    setRemovingMemberId(memberId);
    try {
      const fns = getFunctions(getApp());
      const remove = httpsCallable(fns, 'removeHouseholdMember');
      await remove({ householdId: household.id, memberId });
      showToast(t('household.remove_member_success', 'Member removed from the household.'), 'success');
      dispatch({
        type: 'UPDATE_HOUSEHOLD',
        payload: { id: household.id, updates: { memberIds: household.memberIds.filter(id => id !== memberId) } },
      } as any);
    } catch (e: any) {
      console.error('Error removing member:', e);
      showToast(e.message || t('household.remove_member_error', 'Failed to remove member. Please try again.'), 'error');
    } finally {
      setRemovingMemberId(null);
    }
  };

  // 🌟 NEW LOG: Log the evaluation of the disabled prop's parts
  const isHouseholdNameEmpty = !newHouseholdName.trim();
  const isCurrentUserNull = !currentUser;
  const isCreatingHouse = isCreatingHousehold;
  const isDisabledForCreation = isHouseholdNameEmpty || isCurrentUserNull || isCreatingHouse;
  
  

 return (
    <>
    <div className="max-w-page-wide mx-auto p-6 space-y-8 pb-20 animate-in fade-in duration-500">

      {/* 🧭 Dynamic Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-none">
            {activeTab === 'OVERVIEW' && (household?.name || t('household.overview_title_fallback', 'Household Overview'))}
            {activeTab === 'MEMBERS' && t('household.tab_members', 'Member Registry')}
            {activeTab === 'SWITCHER' && t('household.tab_switcher', 'Node Network')}
            {activeTab === 'ANALYTICS' && 'Household Analytics'}
            {activeTab === 'REWARDS' && 'Rewards & Points'}
          </h1>
          <p className="text-2xs font-black text-blue-500 uppercase tracking-[0.3em] mt-3">
            {activeTab === 'OVERVIEW' && (household?.id ? `H-ID: ${household.id.slice(0, 8)} • ACTIVE` : t('household.no_active_info', 'No Active Household Node'))}
            {activeTab === 'MEMBERS' && `Authorized Protocol Management`}
            {activeTab === 'SWITCHER' && `Multi-Tenant Environment Switching`}
            {activeTab === 'ANALYTICS' && `Performance & Contribution Metrics`}
            {activeTab === 'REWARDS' && `Gamification & Incentive Engine`}
          </p>
        </div>
      </div>

      {/* 📑 Tab Navigation */}
      <nav className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-1">
        {[
          ...(household ? [{ id: 'OVERVIEW', icon: Home, label: 'Overview' }] : []),
          ...(household ? [{ id: 'MEMBERS', icon: Users, label: 'Members' }] : []),
          ...(household ? [{ id: 'ANALYTICS', icon: BarChart2, label: 'Analytics' }] : []),
          ...(household ? [{ id: 'REWARDS', icon: Trophy, label: 'Rewards' }] : []),
          { id: 'SWITCHER', icon: LayoutGrid, label: t('household.tab_switcher', 'Switch / Create') },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-4 text-2xs font-black uppercase tracking-widest border-b-2 transition-all ${
              activeTab === tab.id ? 'border-brand text-brand' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </nav>

      {/* --- TAB 1: OVERVIEW (Only render if household is active) --- */}
      {activeTab === 'OVERVIEW' && household && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">

          {/* Row 1: Identity + Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-6">
              <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-10 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                  <Home className="w-32 h-32" />
                </div>
                <div className="relative z-10 space-y-8">
                  <div>
                    <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest mb-4">Household Identity</h3>
                    <div className="flex items-center gap-6">
                      <div className="w-20 h-20 bg-brand rounded-[2rem] flex items-center justify-center text-white shadow-xl shadow-brand/20 font-black text-3xl italic">
                        {household.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="text-3xl font-black text-slate-900 dark:text-white uppercase italic">{household.name}</h4>
                        <p className="text-xs font-bold text-slate-500 mt-1">{household.type || 'Primary Residence'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Population</p>
                      <p className="text-2xl font-black text-slate-900 dark:text-white italic">
                        {household.memberIds.length} <span className="text-sm text-slate-400 font-bold tracking-normal italic uppercase">/ {household.memberLimit || 6} Slots</span>
                      </p>
                    </div>
                    <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Auth Status</p>
                      <p className="text-2xl font-black text-brand italic uppercase">{currentUserRole}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Property Equity Card */}
              {household.property && (
                <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-10 border border-slate-200 dark:border-slate-800 shadow-sm">
                  <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest mb-6 italic">Property Equity</h3>
                  {(() => {
                    const prop = household.property!;
                    const equity = (prop.currentEstimatedValue ?? 0) - (prop.mortgageBalance ?? 0);
                    const equityPct = prop.currentEstimatedValue
                      ? Math.round((equity / prop.currentEstimatedValue) * 100)
                      : 0;
                    const history = prop.equityHistory ?? [];
                    const maxEquity = history.length > 0
                      ? Math.max(...history.map((h: { date: string; estimatedValue: number; mortgageBalance: number }) => h.estimatedValue - h.mortgageBalance))
                      : equity;
                    return (
                      <div className="space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Purchase Price</p>
                            <p className="text-lg font-black text-slate-900 dark:text-white italic">
                              {prop.purchasePrice != null ? `$${prop.purchasePrice.toLocaleString()}` : '—'}
                            </p>
                          </div>
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Est. Value</p>
                            <p className="text-lg font-black text-slate-900 dark:text-white italic">
                              {prop.currentEstimatedValue != null ? `$${prop.currentEstimatedValue.toLocaleString()}` : '—'}
                            </p>
                          </div>
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Mortgage</p>
                            <p className="text-lg font-black text-slate-900 dark:text-white italic">
                              {prop.mortgageBalance != null ? `$${prop.mortgageBalance.toLocaleString()}` : '—'}
                            </p>
                          </div>
                        </div>
                        <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-3xl border border-blue-100 dark:border-blue-800/30 flex items-center justify-between">
                          <div>
                            <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">Equity</p>
                            <p className="text-3xl font-black text-brand dark:text-blue-400 italic">
                              ${equity.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-4xl font-black text-brand dark:text-blue-400 italic">{equityPct}%</p>
                            <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest">owned</p>
                          </div>
                        </div>
                        {history.length > 1 && (
                          <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 italic">Equity History</p>
                            <div className="flex items-end gap-1 h-16">
                              {history.slice(-12).map((h: { date: string; estimatedValue: number; mortgageBalance: number }, i: number) => {
                                const eq = h.estimatedValue - h.mortgageBalance;
                                const heightPct = maxEquity > 0 ? Math.round((eq / maxEquity) * 100) : 0;
                                return (
                                  <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 group relative">
                                    <div
                                      className="w-full bg-blue-500 rounded-t-sm transition-all"
                                      style={{ height: `${Math.max(heightPct, 4)}%` }}
                                    />
                                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                                      ${eq.toLocaleString()}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="lg:col-span-4 space-y-6">
              {/* Invite / Expansion */}
              <div className="bg-brand rounded-[3rem] p-8 text-white shadow-xl shadow-brand/20">
                <h3 className="text-xs font-black uppercase tracking-widest mb-2 flex items-center gap-2 italic">
                  <UserPlus className="w-4 h-4" /> {t('household.invite_title', 'Expansion')}
                </h3>
                <p className="text-2xs font-bold opacity-80 mb-8 uppercase leading-tight">Generate a temporary access token for new household members.</p>

                {activeInvite ? (
                  <div className="bg-white/10 p-6 rounded-3xl border border-white/20 text-center space-y-4">
                    <h4 className="text-4xl font-black tracking-[0.2em] font-mono">{activeInvite.code}</h4>
                    <p className="text-[9px] font-black opacity-60 flex items-center justify-center gap-2 uppercase tracking-widest italic">
                      <Clock className="w-3 h-3" /> Expires: {new Date(activeInvite.expiresAt).toLocaleTimeString()}
                    </p>
                    <Button variant="outline" className="w-full h-12 rounded-2xl border-white/20 text-white font-black hover:bg-white/10" onClick={() => { navigator.clipboard.writeText(activeInvite.code); showToast('Copied!', 'success'); }}>
                      <Copy className="w-4 h-4 mr-2" /> Copy Key
                    </Button>
                  </div>
                ) : (
                  <Button className="w-full h-14 rounded-2xl bg-white text-brand hover:bg-blue-50 font-black uppercase tracking-widest text-xs" onClick={handleCreateInvite} disabled={!isAuthorized || isCreatingInvite}>
                    {isCreatingInvite ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Generate Token'}
                  </Button>
                )}
              </div>

              {/* Subscription Status */}
              <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-5">
                {justUpgraded && (
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl">
                    <PartyPopper className="w-5 h-5 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-sm font-black text-emerald-800 dark:text-emerald-300">You're upgraded!</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">Your new plan is now active.</p>
                    </div>
                  </div>
                )}
                {justManaged && !justUpgraded && (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                    <div>
                      <p className="text-sm font-black text-blue-800 dark:text-blue-300">Subscription updated</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">Your changes may take a moment to reflect.</p>
                    </div>
                  </div>
                )}
                <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
                  <CreditCard className="w-4 h-4" /> Subscription
                </h3>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                    household.subscription?.tier === SubscriptionTier.PREMIUM ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' :
                    household.subscription?.tier === SubscriptionTier.BASIC ? 'bg-blue-100 dark:bg-blue-900/30 text-brand' :
                    'bg-slate-100 dark:bg-slate-800 text-slate-500'
                  }`}>
                    <Star className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-black text-slate-900 dark:text-white uppercase italic">
                      {household.subscription?.tier ?? SubscriptionTier.FREE}
                    </p>
                    <p className="text-2xs font-bold text-slate-400 uppercase tracking-widest">Current Plan</p>
                  </div>
                </div>
                {(() => {
                  const tier = (household.subscription?.tier ?? SubscriptionTier.FREE).toUpperCase();
                  const limits = PLAN_LIMITS[tier] ?? PLAN_LIMITS['FREE'];
                  const memberCount = household.members?.length ?? 0;
                  const memberPct = Math.min(100, Math.round((memberCount / limits.maxMembers) * 100));
                  return (
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">
                          <span>Members</span>
                          <span>{memberCount} / {limits.maxMembers}</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${memberPct >= 100 ? 'bg-red-500' : memberPct >= 80 ? 'bg-amber-500' : 'bg-brand'}`}
                            style={{ width: `${memberPct}%` }}
                          />
                        </div>
                      </div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Tasks: up to {limits.maxActiveTasks.toLocaleString()} active
                      </p>
                    </div>
                  );
                })()}
                {(!household.subscription?.tier || household.subscription.tier === SubscriptionTier.FREE) ? (
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="w-full h-12 rounded-2xl bg-brand hover:bg-brand-hover text-white font-black uppercase tracking-widest text-xs transition-colors"
                  >
                    Upgrade Plan
                  </button>
                ) : (
                  <button
                    onClick={() => setShowDowngradeModal(true)}
                    disabled={isOpeningPortal}
                    className="w-full h-12 rounded-2xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 font-black uppercase tracking-widest text-xs transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isOpeningPortal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Manage / Cancel Plan
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Connected Features */}
          <div>
            <p className="text-2xs font-black uppercase text-slate-400 tracking-widest mb-4">Connected Features</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Co-Parenting */}
              {!household.coParentingLinkId ? (
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-7 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center shrink-0">
                      <span className="text-rose-500 text-lg">♥</span>
                    </div>
                    <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest italic">Co-Parenting</h3>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 flex-1">Managing shared custody? Link with another household to share schedules and child info.</p>
                  <button
                    onClick={() => navigate('/co-parenting')}
                    className="w-full h-10 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs transition-colors"
                  >
                    Set up
                  </button>
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-7 border border-rose-200 dark:border-rose-800/50 shadow-sm flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center shrink-0">
                      <span className="text-rose-500 text-lg">♥</span>
                    </div>
                    <h3 className="text-2xs font-black uppercase text-rose-400 tracking-widest italic">Co-Parenting Active</h3>
                  </div>
                  <div className="flex-1" />
                  <button
                    onClick={() => navigate('/co-parenting')}
                    className="w-full h-10 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs transition-colors"
                  >
                    Open
                  </button>
                </div>
              )}

              {/* Municipal Services */}
              {!(household as any).subscribedMunicipalityIds?.length ? (
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-7 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                      <span className="text-brand text-lg">🏛️</span>
                    </div>
                    <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest italic">Municipal Services</h3>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 flex-1">Subscribe to your municipality for pickup schedules, community events, and local announcements.</p>
                  <button
                    onClick={() => navigate('/municipal-services')}
                    className="w-full h-10 rounded-2xl bg-brand hover:bg-brand-hover text-white font-black uppercase tracking-widest text-xs transition-colors"
                  >
                    Subscribe
                  </button>
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-7 border border-indigo-200 dark:border-indigo-800/50 shadow-sm flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                      <span className="text-brand text-lg">🏛️</span>
                    </div>
                    <h3 className="text-2xs font-black uppercase text-indigo-400 tracking-widest italic">Municipal Active</h3>
                  </div>
                  <div className="flex-1" />
                  <button
                    onClick={() => navigate('/municipal-services')}
                    className="w-full h-10 rounded-2xl bg-brand hover:bg-brand-hover text-white font-black uppercase tracking-widest text-xs transition-colors"
                  >
                    Manage
                  </button>
                </div>
              )}

              {/* Elder Care */}
              {!(household as any).linkedCareProfileIds?.length ? (
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-7 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center shrink-0">
                      <span className="text-rose-500 text-lg">🫀</span>
                    </div>
                    <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest italic">Elder Care</h3>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 flex-1">Coordinate care for an elder family member — medications, vitals, appointments, and care circle.</p>
                  <button
                    onClick={() => navigate('/elder-care')}
                    className="w-full h-10 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs transition-colors"
                  >
                    Set up
                  </button>
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-7 border border-rose-200 dark:border-rose-800/50 shadow-sm flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center shrink-0">
                      <span className="text-rose-500 text-lg">🫀</span>
                    </div>
                    <h3 className="text-2xs font-black uppercase text-rose-400 tracking-widest italic">Elder Care Active</h3>
                  </div>
                  <div className="flex-1" />
                  <button
                    onClick={() => navigate('/elder-care')}
                    className="w-full h-10 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs transition-colors"
                  >
                    Manage
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Row 3: Family Goals + Commute */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Family Goals */}
            <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-8 border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest italic flex items-center gap-2">
                  <Target className="w-4 h-4" /> Family Goals
                </h3>
                <button
                  onClick={() => setShowAddGoal(v => !v)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-brand text-white rounded-xl text-2xs font-black uppercase tracking-widest hover:bg-brand-hover transition-all"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>

              {showAddGoal && (
                <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl space-y-3">
                  <input
                    type="text"
                    placeholder="Goal title"
                    value={newGoal.title}
                    onChange={e => setNewGoal(p => ({ ...p, title: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-700 text-sm font-medium text-slate-900 dark:text-white border-none focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-black uppercase text-slate-400 mb-1 block">Target count</label>
                      <input type="number" min={1} value={newGoal.targetCount}
                        onChange={e => setNewGoal(p => ({ ...p, targetCount: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-700 text-sm font-medium text-slate-900 dark:text-white border-none focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-[9px] font-black uppercase text-slate-400 mb-1 block">Session (min)</label>
                      <input type="number" min={15} value={newGoal.sessionDurationMins}
                        onChange={e => setNewGoal(p => ({ ...p, sessionDurationMins: parseInt(e.target.value) || 30 }))}
                        className="w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-700 text-sm font-medium text-slate-900 dark:text-white border-none focus:outline-none" />
                    </div>
                  </div>
                  <select value={newGoal.targetFrequency}
                    onChange={e => setNewGoal(p => ({ ...p, targetFrequency: e.target.value as 'weekly' | 'bi_weekly' | 'monthly' }))}
                    className="w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-700 text-sm font-medium text-slate-900 dark:text-white border-none focus:outline-none">
                    <option value="weekly">Weekly</option>
                    <option value="bi_weekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  <button onClick={handleAddGoal} disabled={isSavingGoal || !newGoal.title.trim()}
                    className="w-full py-2 bg-brand text-white rounded-xl text-2xs font-black uppercase tracking-widest hover:bg-brand-hover transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                    {isSavingGoal ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {isSavingGoal ? 'Saving...' : 'Save Goal'}
                  </button>
                </div>
              )}

              {goalsLoading ? (
                <div className="flex justify-center py-6"><LoadingSpinner size="sm" /></div>
              ) : familyGoals.length === 0 ? (
                <p className="text-[11px] text-slate-400 text-center py-4">No family goals yet. Add one above.</p>
              ) : (
                <div className="space-y-3">
                  {familyGoals.map(goal => {
                    const pct = Math.min(100, Math.round((goal.completedCount / goal.targetCount) * 100));
                    return (
                      <div key={goal.id} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-black text-slate-900 dark:text-white">{goal.title}</p>
                          <button onClick={() => handleIncrementGoal(goal)}
                            className="px-3 py-1 bg-brand text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-brand-hover transition-all">
                            Log
                          </button>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 mb-1.5">
                          <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">
                          {goal.completedCount}/{goal.targetCount} · {goal.targetFrequency}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Commute Settings */}
            <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-8 border border-slate-200 dark:border-slate-800 shadow-sm">
              <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest mb-4 italic">Commute Settings</h3>
              <p className="text-[11px] text-slate-400 mb-4">Base commute time used to calculate daily departure estimates.</p>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={0}
                  max={240}
                  value={commuteBaseMinutes}
                  onChange={(e) => setCommuteBaseMinutes(parseInt(e.target.value) || 0)}
                  className="flex-1 p-4 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-2xl border-none font-bold text-sm text-center"
                  placeholder="Minutes"
                />
                <span className="text-2xs font-black uppercase text-slate-400 tracking-widest">min</span>
              </div>
              <button
                onClick={handleSaveCommuteMinutes}
                disabled={isSavingCommute || !isAuthorized}
                className="mt-4 w-full h-11 rounded-2xl bg-brand hover:bg-brand-hover text-white font-black uppercase tracking-widest text-xs transition-colors disabled:opacity-50"
              >
                {isSavingCommute ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

        </div>
      )}

      {activeTab === 'MEMBERS' && household && (
        <div className="max-w-4xl animate-in fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest italic">Access Control Registry</h3>
              {householdMembersForDisplay.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleLeaveHousehold}
                  disabled={isLeavingHousehold} // Disable during processing
                  className="text-rose-500 border-rose-500/50 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-xs font-black uppercase tracking-widest rounded-xl h-9"
                >
                  {isLeavingHousehold ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <DoorOpen className="w-4 h-4 mr-2" />} {isOwner ? t('household.leave_household_owner', 'Leave Household Node') : t('household.leave_household_member', 'Leave Household')}
                </Button>
              )}
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {(householdMembersForDisplay || []).map((member) => {
                const isMe = member.userId === currentUserId;
                return (
                  <div key={member.userId} className="p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center font-black text-slate-500 italic uppercase">
                        {member.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-black text-slate-900 dark:text-white uppercase italic flex items-center gap-2">
                          {member.name} {isMe && <span className="text-[8px] bg-brand text-white px-2 py-0.5 rounded uppercase font-black tracking-widest not-italic">YOU</span>}
                        </p>
                        <p className="text-2xs font-bold text-slate-400 mt-0.5">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <span className="text-2xs font-black text-blue-500 uppercase tracking-widest block">{member.role}</span>
                        <span className="text-[9px] font-bold text-slate-400 block mt-0.5 tracking-tighter uppercase italic">Joined {new Date(member.joinedAt || '').toLocaleDateString()}</span>
                        {memberPoints.length > 0 && (
                          <span className="text-[9px] font-black text-amber-500 block mt-0.5">⚡ {memberPoints.find(mp => mp.userId === member.userId)?.totalPoints ?? 0} XP</span>
                        )}
                      </div>
                      {isAuthorized && !isMe && member.role !== Role.OWNER && (
                        <button
                          onClick={() => handleRemoveMember(member.userId)}
                          disabled={removingMemberId === member.userId}
                          className="p-2 text-slate-300 hover:text-rose-500 transition-colors disabled:opacity-40 disabled:cursor-wait"
                        >
                          {removingMemberId === member.userId
                            ? <RefreshCw className="w-5 h-5 animate-spin" />
                            : <Trash2 className="w-5 h-5" />}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Ownership Transfer Prompt/Modal */}
            {showOwnershipTransferPrompt && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 max-w-lg w-full shadow-2xl space-y-6">
                  <h3 className="text-xl font-black text-slate-900 dark:text-white">{t('household.transfer_ownership_title', 'Transfer Ownership')}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{t('household.transfer_ownership_description', 'You are the owner of this household and there are other members. To leave, you must first transfer ownership to another member.')}</p>

                  <div className="space-y-2">
                    <label htmlFor="newOwner" className="block text-xs font-bold uppercase tracking-widest text-slate-500">{t('household.select_new_owner', 'Select New Owner')}</label>
                    <select
                      id="newOwner"
                      value={newOwnerCandidateId || ''}
                      onChange={(e) => setNewOwnerCandidateId(e.target.value)}
                      className="w-full p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                    >
                      <option value="" disabled>{t('household.choose_member', 'Choose a member')}</option>
                      {householdMembersForDisplay
                        .filter(member => member.userId !== currentUserId && member.role !== Role.OWNER) // Can't transfer to self or existing owner
                        .map(member => (
                          <option key={member.userId} value={member.userId}>
                            {member.name} ({member.role})
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setShowOwnershipTransferPrompt(false)} className="rounded-xl px-6 h-10 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700">
                      {t('household.cancel', 'Cancel')}
                    </Button>
                    <Button
                      onClick={handleTransferOwnership}
                      disabled={!newOwnerCandidateId || isLeavingHousehold}
                      isLoading={isLeavingHousehold}
                      className="rounded-xl px-6 h-10 bg-brand hover:bg-brand-hover text-white"
                    >
                      {isLeavingHousehold ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />} {t('household.transfer_button', 'Transfer & Leave')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- TAB 3: ANALYTICS --- */}
      {activeTab === 'ANALYTICS' && household && (
        <div className="animate-in fade-in slide-in-from-bottom-4">
          {analyticsLoading ? (
            <div className="grid gap-6 md:grid-cols-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse rounded-[2rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                  <div className="mb-6 h-5 w-32 rounded bg-slate-100 dark:bg-slate-800" />
                  <div className="space-y-3">
                    <div className="h-4 w-full rounded bg-slate-100 dark:bg-slate-800" />
                    <div className="h-4 w-3/4 rounded bg-slate-100 dark:bg-slate-800" />
                    <div className="h-4 w-1/2 rounded bg-slate-100 dark:bg-slate-800" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Task Completion Bar Chart */}
              <div className="rounded-[2rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h2 className="mb-6 text-sm font-black uppercase tracking-widest text-slate-400">Task Completion — Last 14 Days</h2>
                {taskCompletionByDay.some(d => d.total > 0) ? (
                  <div className="overflow-x-auto">
                    <svg viewBox="0 0 504 140" className="w-full max-w-full" preserveAspectRatio="xMidYMid meet">
                      {[20, 40, 60, 80].map(y => (
                        <line key={y} x1="0" y1={y} x2="504" y2={y} stroke="rgb(51,65,85)" strokeWidth="1" strokeDasharray="4 2" />
                      ))}
                      {taskCompletionByDay.map((day, i) => {
                        const x = i * 36 + 4;
                        const totalHeight = (day.total / analyticsMaxTask) * 80;
                        const completedHeight = day.total > 0 ? (day.completed / day.total) * totalHeight : 0;
                        const label = new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' });
                        return (
                          <g key={day.date}>
                            <rect x={x} y={80 - totalHeight} width={28} height={totalHeight} fill="rgb(51,65,85)" rx={4} />
                            <rect x={x} y={80 - completedHeight} width={28} height={completedHeight} fill="rgb(99,102,241)" rx={4} />
                            <text x={x + 14} y={105} textAnchor="middle" fill="rgb(148,163,184)" fontSize="10" fontFamily="sans-serif">{label}</text>
                            <text x={x + 14} y={Math.max(5, 75 - totalHeight)} textAnchor="middle" fill="rgb(226,232,240)" fontSize="10" fontFamily="sans-serif">{day.completed}/{day.total}</text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                ) : (
                  <p className="py-8 text-center text-slate-400 text-sm">No tasks created in this period</p>
                )}
              </div>

              {/* Contribution Scores */}
              <div className="rounded-[2rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h2 className="mb-6 text-sm font-black uppercase tracking-widest text-slate-400">Contribution Scores</h2>
                {contributionByMember.length > 0 ? (
                  <div className="space-y-4">
                    {contributionByMember.map(member => (
                      <div key={member.memberId} className="flex items-center gap-3">
                        <div className="w-24 truncate text-sm text-slate-700 dark:text-slate-300">{member.name}</div>
                        <div className="flex-1 h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                          <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${(member.score / analyticsMaxMember) * 100}%` }} />
                        </div>
                        <div className="w-8 text-right text-xs text-slate-400">{member.score}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-8 text-center text-slate-400 text-sm">No contribution data yet</p>
                )}
              </div>

              {/* Items by Category */}
              <div className="md:col-span-2 rounded-[2rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h2 className="mb-6 text-sm font-black uppercase tracking-widest text-slate-400">Items by Category</h2>
                {spendByCategory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-full">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-800">
                          <th className="pb-3 text-left text-xs font-black uppercase tracking-widest text-slate-400">Category</th>
                          <th className="pb-3 text-left text-xs font-black uppercase tracking-widest text-slate-400">Count</th>
                          <th className="pb-3 text-left text-xs font-black uppercase tracking-widest text-slate-400">Distribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spendByCategory.map(item => (
                          <tr key={item.category} className="border-b border-slate-100/50 dark:border-slate-800/50 last:border-0">
                            <td className="py-3 text-sm font-bold text-slate-900 dark:text-white">{item.category}</td>
                            <td className="py-3 text-sm text-slate-500">{item.count}</td>
                            <td className="py-3">
                              <div className="w-48 h-3 rounded bg-blue-500/20 overflow-hidden">
                                <div className="h-full rounded bg-blue-500 transition-all" style={{ width: `${(item.count / analyticsMaxCategory) * 100}%` }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="py-8 text-center text-slate-400 text-sm">No inventory data yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- TAB 5: REWARDS --- */}
      {activeTab === 'REWARDS' && household && (
        <div className="space-y-8 animate-in fade-in">
          {rewardsLoading ? (
            <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-800 rounded-2xl animate-pulse" />)}</div>
          ) : (
            <>
              {/* Member Points Leaderboard */}
              <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-8 border border-slate-100 dark:border-slate-800">
                <h3 className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-6">Monthly Points Leaderboard</h3>
                {memberPoints.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">No points recorded this month. Complete tasks to earn points!</p>
                ) : (
                  <div className="space-y-3">
                    {memberPoints.map((mp, idx) => (
                      <div key={mp.userId} className={`flex items-center gap-4 p-4 rounded-2xl ${mp.userId === state.currentUser?.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-slate-50 dark:bg-slate-800/50'}`}>
                        <span className="text-xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}</span>
                        <div className="flex-1">
                          <p className="font-bold text-sm text-slate-900 dark:text-white">{mp.userId === state.currentUser?.id ? 'You' : mp.userId}</p>
                          <p className="text-xs text-slate-500">All-time: {mp.totalPoints} pts</p>
                        </div>
                        <span className="font-black text-brand text-sm">{mp.monthlyPoints} pts</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reward Catalog */}
              <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-8 border border-slate-100 dark:border-slate-800">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xs font-black uppercase tracking-widest text-slate-400">Reward Catalog</h3>
                  {isAuthorized && (
                    <button onClick={() => setShowAddReward(true)} className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-xl hover:bg-brand-hover">
                      <Plus className="w-3.5 h-3.5" /> Add Reward
                    </button>
                  )}
                </div>
                {rewards.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">{isAuthorized ? 'No rewards yet — add some to motivate your household!' : 'No rewards available yet.'}</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rewards.filter(r => r.isActive).map(reward => (
                      <div key={reward.id} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                        <span className="text-2xl">{reward.emoji || '🎁'}</span>
                        <div className="flex-1">
                          <p className="font-bold text-sm text-slate-900 dark:text-white">{reward.name}</p>
                          <p className="text-xs text-blue-500 font-black">{reward.pointCost} points</p>
                        </div>
                        <button onClick={() => handleRedeem(reward)} className="px-3 py-1.5 bg-brand text-white text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-brand-hover">Redeem</button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Add Reward Form */}
                {showAddReward && isAuthorized && (
                  <div className="mt-4 p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl space-y-3 border border-slate-200 dark:border-slate-700">
                    <h4 className="text-2xs font-black uppercase tracking-widest text-slate-400">New Reward</h4>
                    <div className="flex gap-3">
                      <input value={newRewardEmoji} onChange={e => setNewRewardEmoji(e.target.value)} placeholder="🎁" maxLength={2} className="w-16 px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-center text-xl" />
                      <input value={newRewardName} onChange={e => setNewRewardName(e.target.value)} placeholder="Reward name *" className="flex-1 px-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-blue-500" />
                      <input value={newRewardCost} onChange={e => setNewRewardCost(e.target.value)} type="number" placeholder="Points" className="w-24 px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-blue-500" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleAddReward} disabled={!newRewardName || isSavingReward} className="flex-1 py-2 bg-brand text-white font-black text-[9px] uppercase tracking-widest rounded-xl hover:bg-brand-hover disabled:opacity-50">{isSavingReward ? 'Saving...' : 'Add Reward'}</button>
                      <button onClick={() => setShowAddReward(false)} className="px-4 py-2 text-slate-400 hover:text-slate-600 text-sm"><XIcon className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>

              {/* Pending Redemptions (OWNER/ADMIN only) */}
              {isAuthorized && redemptions.filter(r => r.status === 'pending').length > 0 && (
                <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-8 border border-slate-100 dark:border-slate-800">
                  <h3 className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-6">Pending Redemptions</h3>
                  <div className="space-y-3">
                    {redemptions.filter(r => r.status === 'pending').map(rd => (
                      <div key={rd.id} className="flex items-center gap-4 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-2xl border border-amber-100 dark:border-amber-800">
                        <div className="flex-1">
                          <p className="font-bold text-sm text-slate-900 dark:text-white">{rd.memberName} wants: <span className="text-blue-500">{rd.rewardName}</span></p>
                          <p className="text-xs text-slate-500">{rd.pointCost} points</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleResolveRedemption(rd.id, 'approved')} className="p-2 bg-green-500 text-white rounded-xl hover:bg-green-600"><Check className="w-4 h-4" /></button>
                          <button onClick={() => handleResolveRedemption(rd.id, 'rejected')} className="p-2 bg-red-500 text-white rounded-xl hover:bg-red-600"><XIcon className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* --- TAB 4: SWITCHER / CREATE (Always render) --- */}
      {activeTab === 'SWITCHER' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in">
          {/* Display existing households the user is a member of */}
          {(state.households || []).map((h: Household) => {
            const isActive = h.id === state.currentHouseholdId;
            return (
              <div
                key={h.id}
                className={`p-8 rounded-[2.5rem] border-2 transition-all group relative overflow-hidden ${
                  isActive ? 'bg-brand border-brand text-white shadow-xl shadow-brand/20' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-brand'
                }`}
              >
                <div className="relative z-10 space-y-6">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl italic ${isActive ? 'bg-white/20' : 'bg-blue-50 dark:bg-blue-900/30 text-brand'}`}>
                    {h.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className={`text-xl font-black uppercase italic ${isActive ? 'text-white' : 'text-slate-900 dark:text-white'}`}>{h.name}</h4>
                    <p className={`text-2xs font-bold uppercase tracking-widest mt-1 ${isActive ? 'text-white/60' : 'text-slate-400'}`}>{h.memberIds.length} Members</p>
                  </div>
                  {!isActive && (
                    <Button
                      onClick={() => handleSwitchHousehold(h.id)}
                      className="w-full rounded-2xl h-12 font-black uppercase tracking-widest text-2xs bg-slate-900 text-white hover:bg-slate-800"
                    >
                      Activate Node <ArrowRight className="w-3 h-3 ml-2" />
                    </Button>
                  )}
                  {isActive && (
                    <div className="flex items-center gap-2 text-2xs font-black uppercase tracking-widest text-white/80 italic">
                      <Zap className="w-3 h-3 fill-white" /> Currently Active
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Create New Card (Always visible in SWITCHER tab) */}
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border-2 border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
            <h3 className="text-xs font-black uppercase text-brand tracking-widest mb-2 flex items-center gap-2 italic">
              <Building2 className="w-4 h-4" /> {t('household.new_node_initialization', 'New Node Initialization')}
            </h3>
            <p className="text-2xs text-slate-400 font-bold mb-6 uppercase tracking-tight">{t('household.establish_new_node', 'Establish a new household node for your network.')}</p>
            <input
              type="text"
              placeholder={t('household.new_name_placeholder', 'Name')}
              value={newHouseholdName}
              onChange={(e) => {
                setNewHouseholdName(e.target.value);
                setNewHouseholdNameError(null); // Clear error on change
              }}
              className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-center text-xl uppercase tracking-[0.3em] text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {newHouseholdNameError && (
              <p className="flex items-center gap-2 text-rose-500 text-xs font-bold mt-2">
                <AlertCircle className="w-4 h-4" /> {newHouseholdNameError}
              </p>
            )}
            <Button
              onClick={handleInitializeNewHousehold}
              className="w-full h-14 rounded-2xl font-black uppercase tracking-widest bg-brand hover:bg-brand-hover text-white text-xs"
              isLoading={isCreatingHousehold}
              disabled={!newHouseholdName.trim() || !currentUser || isCreatingHousehold}
            >
              {isCreatingHousehold ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} {t('household.create_new_button', 'Initialize New Node')}
            </Button>
          </div>

          {/* Join with Code input area (Always visible in SWITCHER tab) */}
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border-2 border-blue-500/20 shadow-xl space-y-4">
            <h3 className="text-xs font-black uppercase text-brand tracking-widest mb-2 flex items-center gap-2 italic">
              <DoorOpen className="w-4 h-4" /> {t('household.migration_protocol', 'Migration Protocol')}
            </h3>
            <p className="text-2xs text-slate-400 font-bold mb-6 uppercase tracking-tight">{t('household.enter_key_to_merge', 'Enter a key shared by another household to merge nodes.')}</p>
            <input
              type="text"
              placeholder={t('household.code_placeholder', 'INVITE CODE')}
              value={joinCode}
              onChange={(e) => {
                setJoinCode(e.target.value);
                setJoinCodeError(null);
              }}
              className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-center text-xl uppercase tracking-[0.3em] text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
            />
            {joinCodeError && (
              <p className="flex items-center gap-2 text-rose-500 text-xs font-bold mt-2">
                <AlertCircle className="w-4 h-4" /> {joinCodeError}
              </p>
            )}
            <Button
              onClick={handleJoinByCode}
              className="w-full h-14 rounded-2xl font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              disabled={joinCode.length < 5 || isJoining || !currentUser}
              isLoading={isJoining} // 🌟 Ensure isLoading is passed
            >
              {isJoining ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} {t('household.join_button', 'Join Household')}
            </Button>
          </div>
        </div>
      )}

    </div>

    {showDowngradeModal && (
      <DowngradeWarningModal
        currentTier={
          state.households.find(h => h.id === state.currentHouseholdId)?.subscription?.tier
          ?? SubscriptionTier.FREE
        }
        isLoading={isOpeningPortal}
        onConfirm={() => {
          setShowDowngradeModal(false);
          handleOpenBillingPortal();
        }}
        onCancel={() => setShowDowngradeModal(false)}
      />
    )}
    </>
  );
};